import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import leadStatusApi from '../../api/leadStatusApi';
import { useToast } from '../../contexts/ToastContext';

const TrashedLeadStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [statusToActOnId, setStatusToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedStatuses = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await leadStatusApi.getLeadStatuses({ page, perPage, search, includeTrashed: true });
      setStatuses(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch trashed lead statuses:', err);
      setError('Failed to load trashed lead statuses. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrashedStatuses(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedStatuses, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setStatusToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (statusToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await leadStatusApi.restoreLeadStatus(statusToActOnId);
        addToast('Lead status restored successfully!', 'success');
        await fetchTrashedStatuses(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore lead status:', err);
        addToast(err.response?.data?.message || 'Failed to restore lead status. Please try again.', 'error');
        setError('Failed to restore lead status. Please try again.');
      } finally {
        setLoading(false);
        setStatusToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setStatusToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (statusToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await leadStatusApi.permanentDeleteLeadStatus(statusToActOnId);
        addToast('Lead status permanently deleted successfully!', 'success');
        await fetchTrashedStatuses(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete lead status:', err);
        addToast(err.response?.data?.message || 'Failed to permanently delete lead status. Please try again.', 'error');
        setError('Failed to permanently delete lead status. Please try again.');
      } finally {
        setLoading(false);
        setStatusToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setStatusToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed Lead Statuses</h1>
        <Button
          onClick={() => navigate('/lead-status')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to Lead Statuses
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed Lead Statuses"
          id="search-trashed-lead-statuses"
          name="search-trashed-lead-statuses"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by status name..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Status</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Color</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Is Default</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {statuses.length > 0 ? (
                  statuses.map((status) => (
                    <tr key={status.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{status.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{status.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        <div
                          className="w-6 h-6 rounded-full border border-gray-600"
                          style={{ backgroundColor: status.color }}
                        ></div>
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {status.is_default ? 'Yes' : 'No'}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(status.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-lead-status-tooltip-${status.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(status.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-lead-status-tooltip-${status.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-sm text-text-gray">No trashed lead statuses found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this lead status?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this lead status? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedLeadStatus;